import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCategoriesDataView,
    getCategoryState,
    getSubCategoryIds,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CategoryListPageContext } from "@insite/content-library/Pages/CategoryListPage";
import { HomePageContext } from "@insite/content-library/Pages/HomePage";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import themeColors, { themeFontSizes, themeFontWeights, themeIndentSizes } from "@MasterBluePrint/Styles/variables";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

interface OwnProps extends WidgetProps {
    fields: {};
}

const mapStateToProps = (state: ApplicationState) => ({
    categoriesDataView: getCategoriesDataView(state),
    topLevelCategoryIds: getSubCategoryIds(state, emptyGuid),
});

const mapDispatchToProps = {
    loadCategories,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme>;

export interface ShopByCategoryStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    container?: GridContainerProps;
    categoryItem?: GridItemProps;
    innerContainer?: GridContainerProps;
    categoryImageItem?: GridItemProps;
    categoryImageLink?: LinkPresentationProps;
    categoryImage?: LazyImageProps;
    categoryNameLinkItem?: GridItemProps;
    categoryNameLink?: LinkPresentationProps;
    categoryName?: TypographyProps;
    viewMoreLinkItem?: GridItemProps;
    viewMoreLink?: LinkPresentationProps;
}

export const shopByCategoryStyles: ShopByCategoryStyles = {
    centeringWrapper: {
        css: css`
            height: 18.75rem;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    container: {
        gap: 20,
        css: css`
            display: flex;
            flex-wrap: wrap;
            gap: .9375rem .625rem;
            .shopCategory {
                width: 24.3%;
                border-radius: .125rem;
                background-color: ${themeColors.colorBackground};
                box-shadow: 0 0 6px 0 rgba(1, 13, 28, .04);
                padding: .625rem;
                border: 1px solid transparent;
                .category-info {
                    margin-left: 1.5625rem;
                    .learn-more {
                        color: ${themeColors.colorEndeavour};
                        font-size: ${themeFontSizes.size15};
                        font-weight: ${themeFontWeights.fontWeightForBold};
                        letter-spacing: 0;
                        line-height: 1.3125rem;
                        display: block;
                        opacity: 0;
                        span {
                            margin-left: .125rem;
                        }
                    }
                }
                &:hover {
                    border-color: ${themeColors.colorEndeavour};
                    .learn-more {
                        opacity: 1;
                    }
                }

                @media (min-width: 1081px) and (max-width: 1400px) {
                    width: 30%;
                    .category-info {
                        margin-left: 0.5625rem;
                    }
                }
            }

            @media (max-width: 767px) {
                margin: 0 1.1rem 0 1.4375rem;
                .shopCategory {
                    width: 48%;
                    .category-info {
                        margin: 0;
                        .learn-more {
                            display: none;
                        }
                    }
                }
            }

            @media screen and (max-width: 1023px) and (min-width: 768px) {
                margin: 0;
                .shopCategory {
                    width: 32%;
                    .category-info {
                        margin: 0;
                        .learn-more {
                            display: none;
                        }
                    }
                }
            }

            @media screen and (max-width: 1080px) and (min-width: 1024px) {
                margin: 0 1.25rem;
                .shopCategory {
                    width: 32%;
                    .learn-more {
                        display: none !important;
                    }
                }
            }
        `,
    },
    categoryItem: {
        // width is dynamic here
        width: 0,
    },
    innerContainer: {
        gap: 10,
        css: css`
            > div:first-child {
                display: flex;
                justify-content: flex-start;

                @media (max-width: 767px) {
                    justify-content: center;
                }

                @media (min-width: 768px) and (max-width: 1080px) {
                    justify-content: center;
                }
            }
        `,
    },
    categoryImageItem: {
        width: 12,
        css: css`
            display: flex;
            flex-direction: row;
            @media (max-width: 767px) {
                flex-direction: column;
                justify-content: center;
                align-items: center;
                text-align: center;
            }

            @media (min-width: 768px) and (max-width: 1080px) {
                flex-direction: column;
                justify-content: center;
                align-items: center;
                text-align: center;
            }
        `,
    },
    categoryImage: {
        css: css`
            max-width: 4.4375rem;
            height: 4.4375rem;
            @media (max-width: 767px) {
                height: 3.125rem;
            }

            @media (min-width: 768px) and (max-width: 1080px) {
                height: 3.125rem;
            }

            @media (min-width: 1081px) and (max-width: 1400px) {
                max-width: 3.2rem;
                height: 3.2rem;
            }
        `,
    },
    categoryNameLinkItem: {
        width: 12,
        css: css`
            padding-bottom: .5rem;
        `,
    },
    categoryImageLink: {
        css: css`
            outline: none !important;
            &:hover {
                outline: none !important;
            }
        `,
    },
    categoryName: {
        css: css`
            color: ${themeColors.colorBiscay};
            font-size: ${themeFontSizes.size17};
            font-weight: ${themeFontWeights.fontWeightMedium};
            letter-spacing: 0;
            line-height: 1.375rem;
            min-height: 3.875rem;
            overflow: hidden;
            display: block;

            @media (max-width: 767px) {
                font-size: ${themeFontSizes.size15};
                letter-spacing: -.016rem;
                text-align: center;
                min-height: .625rem;
                padding-top: .625rem;
            }

            @media (min-width: 768px) and (max-width: 1080px) {
                font-size: ${themeFontSizes.size15};
                letter-spacing: -.016rem;
                text-align: center;
                min-height: .625rem;
                padding-top: .625rem;
            }
            @media (min-width: 1081px) and (max-width: 1400px) {
                font-size: ${themeFontSizes.size15};
            }
        `,
    },
    viewMoreLinkItem: {
        width: 12,
        css: css`
            font-weight: ${themeFontWeights.fontWeightForBold};
            padding-top: .625rem;
        `,
    },
};

const styles = shopByCategoryStyles;

class ShopByCategory extends React.Component<Props> {
    container = React.createRef<HTMLDivElement>();
    categoryItemStyles: GridItemProps = { ...styles.categoryItem };

    UNSAFE_componentWillMount(): void {
        const { id, categoriesDataView, loadCategories } = this.props;
        if (!categoriesDataView.isLoading && !categoriesDataView.value) {
            loadCategories();
        }
    }

    recalculateWidth = () => {
        if (this.container.current && this.categoryItemStyles) {
            const theme = this.props.theme;
            const width = this.container.current.clientWidth;
            const prevWidth = this.categoryItemStyles.width;
            if (width < theme.breakpoints.values[1]) {
                this.categoryItemStyles.width = 12;
            } else if (width < theme.breakpoints.values[2]) {
                this.categoryItemStyles.width = 6;
            } else if (width < theme.breakpoints.values[3]) {
                this.categoryItemStyles.width = 4;
            } else {
                this.categoryItemStyles.width = 2;
            }

            if (prevWidth !== this.categoryItemStyles.width) {
                this.forceUpdate();
            }
        }
    };

    componentDidMount() {
        this.recalculateWidth();
        window.addEventListener("resize", this.recalculateWidth, true);
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.recalculateWidth);
    }

    render() {
        const { id, categoriesDataView, topLevelCategoryIds } = this.props;
        if (categoriesDataView.isLoading) {
            return (
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
            );
        }

        if (!categoriesDataView.value || !topLevelCategoryIds) {
            return null;
        }

        const categories = categoriesDataView.value.filter(o => topLevelCategoryIds.indexOf(o.id) > -1);

        return (
            <StyledWrapper {...styles.container}>
                {categories.map(category => (
                    <div className="shopCategory" key={category.id.toString()}>
                        <GridContainer {...styles.innerContainer}>
                            <Link href={category.path} {...styles.categoryImageLink}>
                                <GridItem {...styles.categoryImageItem}>
                                    {category.path && (
                                        <LazyImage altText={`${category?.shortDescription} - category image`} src={category.smallImagePath} {...styles.categoryImage} />
                                    )}
                                    <div className="category-info">
                                        <Typography {...styles.categoryName}>{category.shortDescription}</Typography>
                                        <Typography className="learn-more">
                                            {translate("Learn More")}
                                            <span> &#8594; </span>
                                            {/* <Icon src="ArrowRight" size={15} color="#005cb9" /> */}
                                        </Typography>
                                    </div>
                                </GridItem>
                                {/* <GridItem {...styles.categoryNameLinkItem}></GridItem> */}
                            </Link>
                        </GridContainer>
                    </div>
                ))}
                <GridContainer>
                    <GridItem>
                        <StyledWrapper>
                            <Zone zoneName="RichContentShopByCategory" contentId={id} />
                        </StyledWrapper>
                    </GridItem>
                </GridContainer>
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(ShopByCategory)),
    definition: {
        group: "Categories",
    },
};

export default widgetModule;
